import React, { useRef } from 'react'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import { MdClose, MdLocationPin } from 'react-icons/md'
import { FormGroup, Row } from 'react-bootstrap'

import ATHeaderText from '../common/headerText/ATHeaderText'
import ATIconButton from '../common/formComponent/ATIconButton'

import ATInput from '../common/formComponent/ATInput'

import ATDropdown from '../common/formComponent/ATDropdown'
import ATTextButton from '../common/formComponent/ATTextButton'
// import {
//   plantDetailsFormInitialValues,
//   plantFormDropDownOptionsSample,
// } from './PlantConstants'
import { useTranslation } from 'react-i18next'
import {
  convertRequestFormatForCreate,
  convertRequestFormatForEdit,
  GEOLOCATION_VALIDATION_VALUE,
  setInitialState,
} from './PlantConstants'
import { PlantInfo } from '../../interface/response/PlantResponse'
import usePlantDetails from '../../customHooks/plant/usePlantDetails'
import {
  PlantRequest,
  UpdatePlantRequest,
} from '../../interface/request/PlantRequest'
import ATMap from '../common/map/ATMap'
import AddressForm from '../common/addressForm/AddressForm'
import { Address } from '../../interface/response/Address'
import { defaultAddressFormValue } from '../tenants/tenantConstants'
import { validLatitude, validLongitude } from '../../utils/UICommonFunction'

interface PlantDetailsFormProps {
  data?: PlantInfo
  isEdit: boolean | null | undefined
  onCloseModal: () => void
}

const PlantDetailsForm: React.FC<PlantDetailsFormProps> = ({
  data,
  isEdit,
  onCloseModal,
}) => {
  const childRef: any = useRef()
  const mapRef: any = useRef()
  const { t } = useTranslation(['translation'])
  const { associatedVendorList, createPlant, updatePlantInfo } =
    usePlantDetails()
  const isInValidCoordinates = (latitude: any, longitude: any) => {
    if (!validLatitude(latitude) || !validLongitude(longitude)) {
      return true
    }
    return false
  }
  const locateAddress = (latitude: any, longitude: any) => {
    console.log(latitude, longitude)
    if (childRef?.current) {
      childRef?.current?.getLocationData(latitude, longitude)
    }
    if (mapRef?.current) {
      mapRef?.current?.getMapLocation(latitude, longitude)
    }
  }

  const getInitialAddressFormValues = () => {
    if (!data) {
      return defaultAddressFormValue
    }
    const initValues: Address = {
      street: data.address ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      country: data.country ?? '',
      zipCode: data.zip ?? '',
      fullAddress: data.address ?? '',
    }
    return initValues
  }
  return (
    <div className="p-2">
      <ATHeaderText title={isEdit ? t('plant.edit') : t('plant.add')}>
        <ATIconButton
          title="common.close"
          icon={<MdClose size={18} className="text-dark" />}
          onClick={onCloseModal}
          variant="link"
        />
      </ATHeaderText>
      <div className="p-2">
        <Formik
          validateOnMount={true}
          enableReinitialize
          initialValues={setInitialState(data)}
          validationSchema={Yup.object().shape({
            plantCode: Yup.string().nullable(),
            plantName: Yup.string().required(t('form.required')),
            vendorCode: Yup.string().required(t('form.required')),
            latitude: Yup.number()
              .nullable()
              .required(t('form.required'))
              .typeError(t('form.must_be_a_number'))
              .min(
                GEOLOCATION_VALIDATION_VALUE.LATITUDE_MIN,
                t('form.must_be_more_than', {
                  MIN_DIGIT: GEOLOCATION_VALIDATION_VALUE.LATITUDE_MIN,
                }),
              )
              .max(
                GEOLOCATION_VALIDATION_VALUE.LATITUDE_MAX,
                t('form.must_be_less_than', {
                  MAX_DIGIT: GEOLOCATION_VALIDATION_VALUE.LATITUDE_MAX,
                }),
              ),
            longitude: Yup.number()
              .nullable()
              .required(t('form.required'))
              .typeError(t('form.must_be_a_number'))
              .min(
                GEOLOCATION_VALIDATION_VALUE.LONGITUDE_MIN,
                t('form.must_be_more_than', {
                  MIN_DIGIT: GEOLOCATION_VALIDATION_VALUE.LONGITUDE_MIN,
                }),
              )
              .max(
                GEOLOCATION_VALIDATION_VALUE.LONGITUDE_MAX,
                t('form.must_be_less_than', {
                  MAX_DIGIT: GEOLOCATION_VALIDATION_VALUE.LONGITUDE_MAX,
                }),
              ),
            radius: Yup.number()
              .nullable()
              .required(t('form.required'))
              .min(
                GEOLOCATION_VALIDATION_VALUE.RADIUS_MIN,
                t('form.must_be_more_than', {
                  MIN_DIGIT: GEOLOCATION_VALIDATION_VALUE.RADIUS_MIN,
                }),
              )
              .typeError(t('form.must_be_a_number')),
            // address: Yup.string().required(t('form.required')),
            // zip: Yup.string().required(t('form.required')),
            // city: Yup.string().required(t('form.required')),
            // state: Yup.string().required(t('form.required')),
            // country: Yup.string().required(t('form.required')),
            truckLoadingCount: Yup.number().nullable(),
            //truckLoadingCount: Yup.number().nullable(),
            numberOfSilos: Yup.number().required(t('form.required')),
          })}
          onSubmit={(values) => {
            if (isEdit) {
              const updateRequest: UpdatePlantRequest =
                convertRequestFormatForEdit(values)
              updatePlantInfo(updateRequest)
            } else {
              const createRequest: PlantRequest =
                convertRequestFormatForCreate(values)
              createPlant(createRequest)
            }
            onCloseModal()
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <FormGroup className="col-4 py-1">
                  <ATDropdown
                    options={associatedVendorList?.data}
                    title={t('plant.vendorName')}
                    name="vendorCode"
                    onChange={(data: string) => {
                      setFieldValue('vendorCode', data)
                    }}
                    value={values.vendorCode}
                    labelKey="vendorName"
                    valueKey="vendorCode"
                    isError={errors.vendorCode && touched.vendorCode}
                    error={errors.vendorCode}
                  />
                </FormGroup>
                <FormGroup className="col-4 py-1">
                  <ATInput
                    label={t('plant.plant_name')}
                    name="plantName"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.plantName}
                    isError={errors.plantName && touched.plantName}
                    error={errors.plantName}
                    placeholder=""
                  />
                </FormGroup>
                <FormGroup className="col-4 py-1">
                  <ATInput
                    label={t('plant.no_of_silos')}
                    name="numberOfSilos"
                    type="number"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.numberOfSilos}
                    isError={errors.numberOfSilos && touched.numberOfSilos}
                    error={errors.numberOfSilos}
                    placeholder=""
                  />
                </FormGroup>
                <FormGroup className="col-4 py-1">
                  <ATInput
                    label={t('plant.latitude')}
                    name="latitude"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.latitude}
                    isError={errors.latitude && touched.latitude}
                    error={errors.latitude}
                    placeholder=""
                  />
                </FormGroup>
                <FormGroup className="col-4 py-1">
                  <ATInput
                    label={t('plant.longitude')}
                    name="longitude"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.longitude}
                    isError={errors.longitude && touched.longitude}
                    error={errors.longitude}
                    placeholder=""
                  />
                </FormGroup>
                <FormGroup className="col-4 py-1">
                  <ATInput
                    label={t('plant.radius')}
                    name="radius"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.radius}
                    isError={errors.radius && touched.radius}
                    error={errors.radius}
                    placeholder=""
                  />
                </FormGroup>
                <Row className="mt-2 mb-2 py-2">
                  <ATTextButton
                    title={t('Locate')}
                    onPressHandler={() =>
                      locateAddress(values?.latitude, values?.longitude)
                    }
                    isFilledButton={true}
                    disabled={isInValidCoordinates(
                      values?.latitude,
                      values?.longitude,
                    )}
                  />
                </Row>
                <Row className="plant-map py-1 m-1">
                  <ATMap
                    latitude={values?.latitude}
                    longitude={values?.longitude}
                    radius={values?.radius}
                    plantName={values?.plantName}
                    ref={mapRef}
                  />
                </Row>
                <FormGroup className="col-12 py-1">
                  <AddressForm
                    onChangeAddress={(address: Address) => {
                      setFieldValue('address', address?.street)
                      setFieldValue('state', address?.state)
                      setFieldValue('country', address?.country)
                      setFieldValue('city', address?.city)
                      setFieldValue('zip', address?.zipCode)
                    }}
                    initialValues={getInitialAddressFormValues()}
                    ref={childRef}
                    isFromLatLong
                  />
                </FormGroup>
                {/*<FormGroup className="col-6 py-1">
                   <ATInput
                    isMandatory={false}
                     label={t('plant.truck_loading_count')}
                     name="truckLoadingCount"
                     type="number"
                     onChangeHandler={handleChange}
                     onBlurHandler={handleBlur}
                     value={values.truckLoadingCount}
                     isError={
                       errors.truckLoadingCount && touched.truckLoadingCount
                     }
                     error={errors.truckLoadingCount}
                     placeholder=""
                   />
                 </FormGroup> */}
              </Row>
              <div className="d-flex py-3 justify-content-end">
                <div>
                  <ATTextButton
                    title={t('common.cancel')}
                    onPressHandler={onCloseModal}
                    isFilledButton={false}
                  />
                </div>
                <div>
                  <ATTextButton
                    title={isEdit ? t('common.update') : t('common.add')}
                    onPressHandler={handleSubmit}
                    disabled={!isValid}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default PlantDetailsForm
